"use client"

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react"

export type Theme = "light" | "dark" | "system"

interface ThemeContextValue {
  theme: Theme
  setTheme: (t: Theme) => void
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: "system",
  setTheme: () => {},
})

export function useTheme() {
  return useContext(ThemeContext)
}

function applyDark(dark: boolean) {
  document.documentElement.classList.toggle("dark", dark)
}

function resolveSystemDark() {
  return window.matchMedia("(prefers-color-scheme: dark)").matches
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>("system")
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    const stored = localStorage.getItem("theme") as Theme | null
    const initial: Theme = stored === "light" || stored === "dark" ? stored : "system"
    setThemeState(initial)
    if (initial === "system") {
      applyDark(resolveSystemDark())
    } else {
      applyDark(initial === "dark")
    }
    setMounted(true)
  }, [])

  // Listen to OS preference changes while in system mode
  useEffect(() => {
    if (!mounted) return
    const mq = window.matchMedia("(prefers-color-scheme: dark)")
    const handler = (e: MediaQueryListEvent) => {
      if (theme === "system") applyDark(e.matches)
    }
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [mounted, theme])

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next)
    if (next === "system") {
      localStorage.removeItem("theme")
      applyDark(resolveSystemDark())
    } else {
      localStorage.setItem("theme", next)
      applyDark(next === "dark")
    }
  }, [])

  if (!mounted) {
    return (
      <ThemeContext value={{ theme: "system", setTheme: () => {} }}>
        {children}
      </ThemeContext>
    )
  }

  return (
    <ThemeContext value={{ theme, setTheme }}>
      {children}
    </ThemeContext>
  )
}
